import styled from 'styled-components';
import { Suspense, useCallback, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { Typography } from 'design-system/src/components/Typography/Typography';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import Button from 'design-system/src/components/Button/Button';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';

import { createTrackingProps, trackEvent } from 'shared-utils/analytics';

import PageContainer from '../components/PageContainer';
import { client } from '../api';
import { PendingEnrollment } from '../types';
import { ItemViewTopNav } from '../components/tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../components/tableItemDetailViewComponents/Containers';
import SignFormModal from '../components/enrollments/SignFormModal/SignFormModal';
import InitiationSuccessModal from '../components/enrollments/InitiationSuccessModal/InitiationSuccessModal';
import { FormActionContainer, StickyButtonContainer } from '../components/tasks/actions';
import { toast } from 'react-hot-toast';
import CancelEnrollmentConfirmationModal from '../components/enrollments/CancelEnrollmentConfirmationModal/CancelEnrollmentConfirmationModal';
import { EnrollmentInfoTable } from '../components/enrollments/EnrollmentInfoTable';
import { validateEmbeddedFormInfo } from '../lib/embeddedFormInfo';
import { EmbeddedForm } from '../components/EmbeddedForm';

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledInfoBox = styled(InfoBox)`
  flex-direction: column;
`;

/**
 * Page for creating a pending enrollment created by EnrollmentModal
 */
export function PendingEnrollmentPage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <InnerPendingEnrollmentPage />
    </Suspense>
  );
}

function InnerPendingEnrollmentPage() {
  const [showSignFormModal, setShowSignFormModal] = useState(false);
  const [showInitiationSuccessModal, setShowInitiationSuccessModal] = useState(false);
  const [showCancelEnrollmentConfirmationModal, setShowCancelEnrollmentConfirmationModal] = useState(false);
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { enrollmentId } = useParams();
  const pendingEnrollmentQuery = useSuspenseQuery<PendingEnrollment>({
    queryKey: ['pendingEnrollment', enrollmentId],
    queryFn: async () => {
      const res = await client.get(`enrollments/pending/${enrollmentId}`);
      return res.data;
    },
  });

  const initiateEnrollmentMutation = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking enrollment initiation, no real request sent.');

        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      await client.post(`enrollments/${enrollmentId}/initiate`);
    },
    onSuccess: () => {
      setShowInitiationSuccessModal(true);
      queryClient.invalidateQueries({
        queryKey: ['pendingEnrollment', enrollmentId],
      });
      trackEvent('New In-Office Enrollment: Submit Success', {
        enrollment_id: enrollmentId!,
      });
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
      trackEvent('New In-Office Enrollment: Submit Error', {
        enrollment_id: enrollmentId!,
      });
    },
  });

  const cancelEnrollmentMutation = useMutation({
    mutationFn: async () => {
      await client.post(`enrollments/${enrollmentId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['pendingEnrollment', enrollmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['pending-enrollment-forms'],
      });
      toast.custom(() => <Toast variant="success">Enrollment canceled</Toast>);
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
    onSettled: () => {
      // Close the modal if it's open
      setShowCancelEnrollmentConfirmationModal(false);
    },
  });

  if (pendingEnrollmentQuery.isError) {
    throw pendingEnrollmentQuery.error;
  }

  const { status, prescription_id, requires_signature, form } = pendingEnrollmentQuery.data;
  const embeddedFormInfo = validateEmbeddedFormInfo({
    form: form,
  });
  if (!embeddedFormInfo) {
    throw new Error('Invalid embedded form info');
  }

  const buttonAnalyticsName = requires_signature
    ? 'New In-Office Enrollment: Collect Patient Signature Button'
    : 'New In-Office Enrollment: Initiate Enrollment Button';

  const onComplete = useCallback(async () => {
    await initiateEnrollmentMutation.mutateAsync();
  }, [initiateEnrollmentMutation]);

  const notStartedStep = (
    <>
      <SpacedContent {...createTrackingProps({ unmask: true })}>
        <Typography styledAs="bodyLargeSpaceGrotesk" renderedAs="h2" weight={500}>
          Submission instructions
        </Typography>

        <Typography styledAs="bodySmallDMSans">
          A pre-filled enrollment form has been generated with the patient information provided.
        </Typography>
        <Typography styledAs="bodySmallDMSans">
          Upon receiving the incoming prescription for this enrollment, we will complete any remaining sections based on
          the details in the matching prescription.
        </Typography>
        <Typography styledAs="bodySmallDMSans">
          Please review the form below and collect the patient's signature. Complete any remaining sections to your
          liking but please know that it's <strong>optional</strong> as Tandem will autofill the remaining sections upon
          matching the prescription.
        </Typography>
      </SpacedContent>

      <Hairline />

      <StyledInfoBox includeIcon={false} variant="surface" {...createTrackingProps({ unmask: true })}>
        <Typography weight={600}>
          {requires_signature
            ? 'Please review the form to ensure your details are correct. Provide a signature from the patient using the button below to initiate patient enrollment.'
            : 'Please review the form to ensure your details are correct. Use the button below to initiate patient enrollment.'}
        </Typography>
      </StyledInfoBox>

      <FormActionContainer>
        <EmbeddedForm
          formInfo={embeddedFormInfo}
          roleOverride={'Patient'}
          onComplete={embeddedFormInfo.form_source === 'form_filler' ? onComplete : undefined}
        />

        <StickyButtonContainer>
          <ButtonRow align={Alignment.Center}>
            {/* Form Filler forms will attach this action to the complete button. */}
            {embeddedFormInfo.form_source !== 'form_filler' && (
              <Button
                size="sm"
                width="full"
                onClick={() => {
                  if (requires_signature) {
                    setShowSignFormModal(true);
                  } else {
                    initiateEnrollmentMutation.mutate();
                  }
                }}
                isLoading={initiateEnrollmentMutation.isPending}
                {...createTrackingProps({ elementName: buttonAnalyticsName })}
              >
                {requires_signature ? 'Collect patient e-signature' : 'Initiate enrollment form'}
              </Button>
            )}

            <Typography
              styledAs="bodyExtraSmallDMSans"
              color="var(--grayscale-600)"
              {...createTrackingProps({ unmask: true })}
            >
              {requires_signature ? 'After signing' : 'After initiating enrollment'}, this form will become{' '}
              <strong>uneditable</strong> until matched with its prescription.
            </Typography>
          </ButtonRow>
        </StickyButtonContainer>
      </FormActionContainer>
    </>
  );

  const patientConsentReceivedStep = (
    <>
      <SpacedContent {...createTrackingProps({ unmask: true })}>
        <Typography styledAs="bodyLargeSpaceGrotesk" renderedAs="h2" weight={500}>
          This enrollment form is currently pending.
        </Typography>

        <Typography styledAs="bodySmallDMSans">
          We're waiting for the matching prescription to arrive, which usually occurs within 24 hours of it being sent.
          Once matched, you'll be redirected to the prescription page to sign the completed form and manage the
          prescription.
        </Typography>
        <Typography styledAs="bodySmallDMSans">
          Upon receiving the incoming prescription for this enrollment, we will complete any remaining sections based on
          the details in the matching prescription.
        </Typography>
        <Typography styledAs="bodySmallDMSans">No action is needed at this time.</Typography>
      </SpacedContent>

      <Hairline />

      <ButtonRow align={Alignment.Left}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setShowCancelEnrollmentConfirmationModal(true);
          }}
          isLoading={cancelEnrollmentMutation.isPending}
          {...createTrackingProps({ elementName: 'Pending Enrollment Page: Cancel Pending Enrollment Button' })}
        >
          Cancel enrollment
        </Button>
      </ButtonRow>
    </>
  );

  const canceledStep = (
    <SpacedContent {...createTrackingProps({ unmask: true })}>
      <Typography styledAs="bodyLargeSpaceGrotesk" renderedAs="h2" weight={500}>
        This enrollment has been canceled.
      </Typography>
    </SpacedContent>
  );

  const getBodyContent = () => {
    switch (status) {
      case 'not_started':
        return notStartedStep;
      case 'patient_consent_received':
        return patientConsentReceivedStep;
      case 'matched':
        return <Navigate to={`/rxs/${prescription_id}`} replace />;
      case 'canceled':
        return canceledStep;
      default: {
        const _typecheck: never = status;
        return <PageLoadError />;
      }
    }
  };

  return (
    <PageContainer noPadding={true}>
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav noSidebar />

          <ContentOuterWrapper>
            <CenteredContent>
              <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
                New enrollment form
              </Typography>

              <EnrollmentInfoTable enrollment={pendingEnrollmentQuery.data} />

              {getBodyContent()}

              {showSignFormModal && (
                <SignFormModal onClose={() => setShowSignFormModal(false)} analyticsSource="New In-Office Enrollment" />
              )}
              {showInitiationSuccessModal && (
                <InitiationSuccessModal onClose={() => setShowInitiationSuccessModal(false)} />
              )}
              {showCancelEnrollmentConfirmationModal && (
                <CancelEnrollmentConfirmationModal
                  onClose={() => setShowCancelEnrollmentConfirmationModal(false)}
                  onConfirm={cancelEnrollmentMutation.mutate}
                  isLoading={cancelEnrollmentMutation.isPending}
                  enrollment={pendingEnrollmentQuery.data}
                />
              )}
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>
      </BodyContainer>
    </PageContainer>
  );
}
